/**
 * Step 4 — "Get your crew onboard": suggested teammates + email invite.
 *
 * Suggestions come from the signed-in user's calendar
 * (`/api/onboarding/self/team-suggestions/`). Selections and the typed email
 * live in flow state; the invitations themselves are sent by the confirm
 * step, so both CTAs here just advance the wizard.
 */
import { Mail01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { ListCard } from '@/lib/base/list-card';
import type { TeamSuggestion } from '@/lib/self-service/api';
import { useTeamSuggestions } from '@/lib/self-service/api';
import { SelectableRow } from '@/lib/self-service/components/SelectableRow';
import { SkipButton } from '@/lib/self-service/components/SkipButton';
import { useSelfServiceFlowState } from '@/lib/self-service/flowState';
import { useToggleInArray } from '@/lib/self-service/hooks/useToggleInArray';

function getInitials(name: string, email: string): string {
  const source = name.trim() || email.split('@')[0];
  return source
    .split(/[\s.]+/)
    .map(part => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

type SuggestionsState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'empty' }
  | { kind: 'ready'; suggestions: TeamSuggestion[] };

interface SuggestionListProps {
  state: SuggestionsState;
  selected: string[];
  onToggle: (email: string) => void;
}

function SuggestionList({ state, selected, onToggle }: SuggestionListProps) {
  switch (state.kind) {
    case 'loading':
      return (
        <LoadingIndicator
          size='sm'
          label='Finding teammates in your meetings…'
        />
      );
    case 'error':
      return (
        <p className='text-sm text-[var(--color-text-tertiary)]'>
          We couldn't load teammate suggestions. You can still invite people by
          email below.
        </p>
      );
    case 'empty':
      return (
        <p className='text-sm text-[var(--color-text-tertiary)]'>
          No teammates found in your recent meetings yet. Invite them by email
          below.
        </p>
      );
    case 'ready':
      return (
        <ListCard background='primary'>
          {state.suggestions.map(suggestion => (
            <SelectableRow
              key={suggestion.email}
              id={suggestion.email}
              leading={
                <Avatar
                  as='span'
                  size='md'
                  initials={getInitials(suggestion.name, suggestion.email)}
                />
              }
              title={suggestion.name || suggestion.email}
              subtitle={suggestion.email}
              isSelected={selected.includes(suggestion.email)}
              onToggle={onToggle}
            />
          ))}
        </ListCard>
      );
  }
}

interface InviteStepProps {
  onContinue: () => void;
}

export function InviteStep({ onContinue }: InviteStepProps) {
  const { state, updateState } = useSelfServiceFlowState();
  const suggestionsQuery = useTeamSuggestions();

  const suggestionsState = useMemo<SuggestionsState>(() => {
    if (suggestionsQuery.isPending) {
      return { kind: 'loading' };
    }
    if (suggestionsQuery.isError) {
      return { kind: 'error' };
    }
    const { suggestions } = suggestionsQuery.data;
    return suggestions.length === 0
      ? { kind: 'empty' }
      : { kind: 'ready', suggestions };
  }, [
    suggestionsQuery.isPending,
    suggestionsQuery.isError,
    suggestionsQuery.data,
  ]);

  const handleTeammateChange = useCallback(
    (next: string[]) => updateState({ selectedTeammates: next }),
    [updateState]
  );
  const handleTeammateToggle = useToggleInArray(
    state.selectedTeammates,
    handleTeammateChange
  );

  // Persisted in flow state (like every other wizard field) so it survives
  // navigating away from this step and a refresh.
  const handleInviteEmailChange = useCallback(
    (value: string) => updateState({ inviteEmail: value }),
    [updateState]
  );

  const inviteCount =
    state.selectedTeammates.length + (state.inviteEmail.trim() ? 1 : 0);

  return (
    <div className='flex flex-col gap-6'>
      <SuggestionList
        state={suggestionsState}
        selected={state.selectedTeammates}
        onToggle={handleTeammateToggle}
      />

      <Input
        className='w-full'
        placeholder='Invite someone else by email'
        aria-label='Invite someone else by email'
        type='email'
        value={state.inviteEmail}
        onChange={handleInviteEmailChange}
        name='inviteEmail'
        icon={Mail01}
        tooltip='They get an email invite once your workspace is set up.'
      />

      <div className='flex flex-col gap-4'>
        <Button
          size='lg'
          className='w-full'
          onClick={onContinue}
          isDisabled={inviteCount === 0}
        >
          Send {inviteCount} {inviteCount === 1 ? 'invite' : 'invites'}
        </Button>
        <SkipButton onSkip={onContinue} />
      </div>
    </div>
  );
}
